/** The multiplier at which the rising-tension cue starts. */
export const COUNTDOWN_TRIGGER_MULTIPLIER = 2

/**
 * A display reading older than this is treated as ABSENT — the hero's rAF loop
 * has parked or unmounted, so the number is frozen.
 *
 * Deliberately MUCH looser than the 400ms the other crash surfaces use, because
 * the consequence here is not a stale number: stopping the cue means the next
 * tick restarts it from the top of the 13.5s BUILD, dropping the player back to
 * slow entry ticks at the highest-tension moment of the round. A rAF loop that
 * skips a couple of frames under load is normal; a beacon that is genuinely
 * dead stays dead well past this.
 *
 * The runaway case this used to guard is covered properly by MAX_CUE_MS below,
 * which cannot be defeated by a frozen-but-fresh reading.
 */
export const DISPLAY_STALE_MS = 1_500

/**
 * Hard ceiling on how long the cue may sound in one round, whatever else is
 * true.
 *
 * The cue LOOPS, so it is the one sound that can outlive its round: if the
 * crash event never arrives (engine dies mid-round, socket wedges) every other
 * condition here keeps holding — the round still reads live and the beacon
 * still ticks — and the loop plays indefinitely. A round cannot legitimately
 * outlive the curve by this much, so past it the cue is wrong by definition.
 */
export const MAX_CUE_MS = 150_000

/**
 * Whether the rising-tension cue should be sounding right now.
 *
 * DERIVED every frame, never latched — there is no "started" flag to get stuck
 * on. If any condition stops holding the sound stops, which is the behaviour
 * you want from something that loops indefinitely.
 *
 * Reads the VISUAL multiplier (the frame beacon), never the event clock. The
 * event clock leads the video by syncDelta, so triggering on it would start the
 * sound before the player sees 2x on screen — the tell that every other crash
 * surface is deliberately gated against. The bust thump is already gated the
 * same way, so the two cues stay on one clock.
 */
export const resolveCountdownCue = (input: {
  /** Latest DISPLAY multiplier, or null when the beacon has nothing. */
  multiplier: number | null
  /** Age of that reading in ms. */
  ageMs: number
  /** The crash is on the viewer's frames — the bust thump takes over here. */
  visualCrashed: boolean
  /** The round is actually running. */
  isLive: boolean
  /** How long the cue has been sounding this round, ms. */
  playingForMs?: number
  /**
   * The display clock is riding the EVENT-CLOCK fallback (beacon dead —
   * review 2026-08-17). The readings are then fresh but lead the frames by
   * the stream sync-delta, so the trigger would fire before the player sees
   * 2x on screen — exactly what this module's contract forbids. Staleness
   * cannot catch it (the hero republishes every rAF, ageMs≈0), so the
   * degraded state is passed explicitly and the cue holds silent.
   */
  displayDegraded?: boolean
}): boolean => {
  if (!input.isLive) return false
  if (input.displayDegraded) return false
  if ((input.playingForMs ?? 0) > MAX_CUE_MS) return false
  if (input.visualCrashed) return false
  if (input.multiplier === null || !Number.isFinite(input.multiplier)) return false
  if (input.ageMs > DISPLAY_STALE_MS) return false

  return input.multiplier >= COUNTDOWN_TRIGGER_MULTIPLIER
}
